import { useEffect, useState } from 'react';
import logoUrl from './assets/tbo_logo_light_transparent.png';
import { ApiError, followup, getAssessment, startAssessment, submitAnswers } from './api.js';
import { DiscoverIntakeForm } from './components/DiscoverIntakeForm.js';
import { IntakeForm } from './components/IntakeForm.js';
import { ModeChooser } from './components/ModeChooser.js';
import { QuestionsForm } from './components/QuestionsForm.js';
import { Results } from './components/Results.js';
import { Spinner } from './components/Spinner.js';
import { Stepper } from './components/Stepper.js';
import { captureAttribution, readAttribution } from './attribution.js';
import { resolveMode } from './mode.js';
import { clearId, readSavedId, saveId } from './persistence.js';
import type {
  Answer,
  AnyIntake,
  AssessmentMode,
  Depth,
  GeneratedQuestion,
  StartResponse,
  SubmitResponse,
} from './types.js';

type Step =
  | 'loading'
  | 'intake'
  | 'analysing'
  | 'questions'
  | 'following-up'
  | 'submitting'
  | 'results';

const STEP_INDEX: Record<Step, number> = {
  loading: 0,
  intake: 0,
  analysing: 0,
  questions: 1,
  'following-up': 1,
  submitting: 1,
  results: 2,
};

/** The active question round on screen. */
interface RoundView {
  round: number;
  questions: GeneratedQuestion[];
  headerMessage?: string;
}

/** Base assessment info that outlives individual rounds. */
interface Session {
  assessmentId: string;
  mode: AssessmentMode;
  depth: Depth;
  maxRounds: number;
}

export function App(): React.JSX.Element {
  const [step, setStep] = useState<Step>(() => (readSavedId() ? 'loading' : 'intake'));
  const [error, setError] = useState<string | null>(null);
  // The mode the intake form should use before an assessment exists (null → chooser).
  const [activeMode, setActiveMode] = useState<AssessmentMode | null>(() => resolveMode());
  const [session, setSession] = useState<Session | null>(null);
  const [round, setRound] = useState<RoundView | null>(null);
  const [savedAnswers, setSavedAnswers] = useState<Answer[]>([]);
  const [result, setResult] = useState<SubmitResponse | null>(null);

  // Capture campaign attribution from the landing URL before anything rewrites it
  // (saveId does a replaceState once an assessment exists). First touch wins.
  useEffect(() => {
    captureAttribution();
  }, []);

  // Resume from a previously saved / linked assessment id.
  useEffect(() => {
    const id = readSavedId();
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const state = await getAssessment(id);
        if (cancelled) return;
        saveId(state.assessmentId);
        setActiveMode(state.mode);
        setSession({
          assessmentId: state.assessmentId,
          mode: state.mode,
          depth: state.depth,
          maxRounds: state.max_rounds,
        });
        if (state.stage === 'results' && state.result) {
          setResult(state.result);
          setStep('results');
        } else {
          setRound({
            round: state.round,
            questions: state.questions,
            headerMessage:
              state.round === 1 ? state.intro_message : 'A few more questions to sharpen your result.',
          });
          setSavedAnswers(state.answers ?? []);
          setStep('questions');
        }
      } catch {
        if (cancelled) return;
        clearId();
        setStep('intake');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Post height to the embedding page so the WordPress iframe can auto-resize.
  useEffect(() => {
    if (window.parent === window) return;
    const height = document.documentElement.scrollHeight;
    window.parent.postMessage({ type: 'tbo-assessment-height', height }, '*');
  }, [step, error, result, round, activeMode]);

  function adoptStart(res: StartResponse): void {
    setSession({
      assessmentId: res.assessmentId,
      mode: res.mode,
      depth: res.depth,
      maxRounds: res.max_rounds,
    });
    setRound({ round: res.round, questions: res.questions, headerMessage: res.intro_message });
    setSavedAnswers([]);
  }

  async function handleIntake(intake: AnyIntake, depth: Depth): Promise<void> {
    if (!activeMode) return;
    setError(null);
    setStep('analysing');
    try {
      const res = await startAssessment(intake, depth, activeMode, readAttribution());
      saveId(res.assessmentId);
      adoptStart(res);
      setStep('questions');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unexpected error.');
      setStep('intake');
    }
  }

  async function runSubmit(assessmentId: string, answers?: Answer[]): Promise<void> {
    setStep('submitting');
    const res = await submitAnswers(assessmentId, answers);
    setResult(res);
    setStep('results');
  }

  async function handleAnswers(answers: Answer[]): Promise<void> {
    if (!session) return;
    setError(null);
    try {
      if (session.depth === 'fast') {
        await runSubmit(session.assessmentId, answers);
        return;
      }
      setStep('following-up');
      const res = await followup(session.assessmentId, answers);
      if (res.status === 'more_questions' && res.questions) {
        setRound({
          round: res.round ?? (round?.round ?? 1) + 1,
          questions: res.questions,
          headerMessage: res.transition_message,
        });
        setSavedAnswers([]);
        setStep('questions');
      } else {
        await runSubmit(session.assessmentId);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unexpected error.');
      setStep('questions');
    }
  }

  function handleRestart(): void {
    clearId();
    setSession(null);
    setRound(null);
    setSavedAnswers([]);
    setResult(null);
    setError(null);
    setActiveMode(resolveMode());
    setStep('intake');
  }

  const canRestart = step === 'questions' || step === 'results';
  const isDeep = session?.depth === 'deep';
  const resultMode = session?.mode ?? activeMode ?? 'evaluate';

  return (
    <div className="tbo-widget">
      <header className="tbo-header">
        <img className="tbo-logo" src={logoUrl} alt="The Blue Owls Solutions" />
        <span className="tbo-tag">AI Use-Case Assessment</span>
      </header>

      <Stepper active={STEP_INDEX[step]} />

      {error && <div className="tbo-error">{error}</div>}

      <main className="tbo-main">
        {step === 'loading' && (
          <Spinner
            title="Restoring your assessment…"
            subtitle="Picking up right where you left off."
          />
        )}

        {step === 'intake' && !activeMode && <ModeChooser onChoose={setActiveMode} />}
        {step === 'intake' && activeMode === 'evaluate' && <IntakeForm onSubmit={handleIntake} />}
        {step === 'intake' && activeMode === 'discover' && (
          <DiscoverIntakeForm onSubmit={handleIntake} />
        )}

        {step === 'analysing' && (
          <Spinner
            title="Analysing your website…"
            subtitle="Reading your site and your answers to prepare a few sharp follow-up questions. This takes up to 30 seconds."
          />
        )}

        {step === 'questions' && round && (
          <QuestionsForm
            key={round.round}
            questions={round.questions}
            headerMessage={round.headerMessage}
            roundLabel={isDeep ? `Round ${round.round} of up to ${session?.maxRounds}` : undefined}
            submitLabel={isDeep ? 'Continue →' : 'Get my assessment →'}
            initialAnswers={savedAnswers}
            onSubmit={handleAnswers}
          />
        )}

        {step === 'following-up' && (
          <Spinner
            title="Reviewing your answers…"
            subtitle="The AI is deciding whether it needs anything else before scoring."
          />
        )}

        {step === 'submitting' && (
          <Spinner
            title="Scoring your assessment…"
            subtitle="Weighing feasibility, ROI and adoption, and preparing your one-pager."
          />
        )}

        {step === 'results' && result && <Results result={result} mode={resultMode} />}
      </main>

      {canRestart && (
        <footer className="tbo-footer">
          <button type="button" className="tbo-link" onClick={handleRestart}>
            Start a new assessment
          </button>
        </footer>
      )}
    </div>
  );
}
